import React, { useState, useEffect, useRef } from 'react';

const MODULES = {
  'Product Management': {
    icon: '📦', color: '#2563eb', product: 'Mart CMS',
    desc: 'Complete product catalogue management from categories to vendor mapping.',
    items: ['Categories & Subcategories', 'Product Variants', 'Pricing Rules & Tiers', 'Tax Configuration (GST)', 'SKU Management', 'Vendor Mapping', 'Product Images & Descriptions', 'Bulk Product Import'],
  },
  'Vendor Management': {
    icon: '🏭', color: '#4f46e5', product: 'Mart CMS',
    desc: 'End-to-end supplier and procurement management.',
    items: ['Supplier Directory', 'Purchase Order Creation', 'Purchase Invoices', 'Vendor Payment History', 'Vendor Stock Tracking', 'Supplier Performance Reports', 'Multi-Vendor Support', 'Vendor Communication Log'],
  },
  'Inventory Management': {
    icon: '🗃', color: '#0891b2', product: 'Mart CMS',
    desc: 'Real-time inventory control across all warehouses and stores.',
    items: ['Real-Time Inventory Tracking', 'Warehouse Management', 'Stock Movement History', 'Purchase Entry Logging', 'Vendor Stock Levels', 'Low Stock Alerts', 'Inventory Sync Across Stores', 'Expiry & Batch Tracking'],
  },
  'Store Management': {
    icon: '🏪', color: '#059669', product: 'Mart CMS',
    desc: 'Configure and manage all your branches from one portal.',
    items: ['Store Configuration', 'Branch Management', 'Operating Hours Setup', 'Staff Roles & Permissions', 'Store-Level Analytics', 'Tax & Compliance Settings', 'Delivery Zone Configuration', 'Brand & Theme Settings'],
  },
  'Order Management': {
    icon: '📋', color: '#d97706', product: 'Both',
    desc: 'Unified order management across all channels and platforms.',
    items: ['POS Counter Orders', 'Mobile App Orders', 'Delivery Orders', 'Order Reconciliation', 'Bulk Orders', 'Party Orders', 'Order History', 'Return & Refund Orders'],
  },
  'Offers Management': {
    icon: '🎉', color: '#dc2626', product: 'Mart CMS',
    desc: 'Create and manage promotions to drive sales.',
    items: ['Discount Coupons', 'Product-Level Offers', 'Buy X Get Y Promotions', 'Category Discounts', 'Customer-Specific Offers', 'Scheduled Promotions', 'Advertisement Banners', 'Loyalty Points'],
  },
  'Delivery Management': {
    icon: '🚚', color: '#7c3aed', product: 'Mart CMS',
    desc: 'Full-cycle delivery operations from assignment to proof of delivery.',
    items: ['Integrated Delivery Module', 'Order-to-Rider Assignment', 'Real-Time Delivery Tracking', 'Proof of Delivery Capture', 'Delivery Zone Management', 'Rider Performance Reports', 'Customer Delivery Notifications', 'COD Management'],
  },
  'POS Management': {
    icon: '🧾', color: '#2563eb', product: 'POS-Lite',
    desc: 'Fast, intuitive billing at the counter with all the tools your staff needs.',
    items: ['POS Register Management', 'Open & Close Register', 'Till Management', 'Barcode Scan Checkout', 'Product Search & Filter', 'Quick Commerce Mode', 'Cart & Checkout', 'Receipt Printing'],
  },
  'Payment Modes': {
    icon: '💳', color: '#059669', product: 'Both',
    desc: 'Accept every payment type your customers prefer.',
    items: ['Cash Payments', 'Online (UPI / Cards)', 'Cash on Delivery (COD)', 'Split Payment Support', 'Payment Reconciliation', 'Refund Processing', 'Daily Settlement Reports', 'Payment Gateway Integration'],
  },
};

const MODULE_KEYS = Object.keys(MODULES);

export default function Modules() {
  const [active, setActive] = useState(MODULE_KEYS[0]);
  const mod = MODULES[active];
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const nav = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="section" id="modules" style={{ background: '#f9fafb' }}>
      <div className="container">
        <div ref={ref} className="reveal" style={{ maxWidth: 600, marginBottom: 52 }}>
          <span className="eyebrow">All Modules</span>
          <h2 className="t-section" style={{ color: '#0c1220' }}>Everything working as one.</h2>
          <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, marginTop: 16 }}>Click any module to explore its full capability set.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }}>
          {/* Module list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {MODULE_KEYS.map(key => {
              const m = MODULES[key];
              const isActive = active === key;
              return (
                <button key={key} onClick={() => setActive(key)} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', borderRadius: 10, border: 'none',
                  background: isActive ? 'white' : 'transparent',
                  boxShadow: isActive ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
                  cursor: 'pointer', textAlign: 'left',
                  borderLeft: isActive ? `3px solid ${m.color}` : '3px solid transparent',
                  transition: 'all 0.18s',
                }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{m.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: 13.5, color: isActive ? '#111827' : '#4b5563', lineHeight: 1.2 }}>{key}</div>
                    <div style={{ fontSize: 10, color: isActive ? m.color : '#9ca3af', fontWeight: 600, marginTop: 2 }}>{m.product}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Module detail */}
          <div style={{ background: 'white', borderRadius: 20, padding: '36px', border: '1.5px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', minHeight: 400 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 60, height: 60, borderRadius: 16, background: mod.color + '12', border: `1.5px solid ${mod.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{mod.icon}</div>
                <div>
                  <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 24, color: '#111827', letterSpacing: '-0.5px' }}>{active}</h3>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <span style={{ background: mod.color + '12', color: mod.color, border: `1px solid ${mod.color}25`, borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>{mod.product}</span>
                  </div>
                </div>
              </div>
            </div>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid #f3f4f6' }}>{mod.desc}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {mod.items.map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: mod.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5 3.5-4" stroke={mod.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
                  </span>
                  <span style={{ fontSize: 13.5, color: '#374151', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid #f3f4f6', display: 'flex', gap: 12 }}>
              <button className="btn btn-blue btn-sm" onClick={() => nav('contact')}>Request Demo for this Module</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`@media(max-width:800px){.modules-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
